import { Period } from '@spike/model';
import { Promocode, SavePromocode } from '@spike/promocode-model';
import moment from 'moment';
import { Moment } from 'moment-timezone';

export const getTimeOptionsForDateRange = (
    selectedDate: Moment | null,
    dateLimits?: Period
): Array<{ id: string; label: string }> => {
    const options: { id: string; label: string }[] = [];

    if (!selectedDate) {
        return options;
    }

    const timeZone = selectedDate.tz() || 'UTC';

    let startHour = 0;
    let startMinute = 0;
    let endHour = 23;
    let endMinute = 59;

    if (dateLimits?.from && dateLimits?.to) {
        const selectedDateFormatted = selectedDate.format('YYYY-MM-DD');
        const fromDateFormatted = dateLimits.from.format('YYYY-MM-DD');
        const toDateFormatted = dateLimits.to.format('YYYY-MM-DD');

        if (selectedDateFormatted === fromDateFormatted) {
            startHour = dateLimits.from.hour();
            startMinute = dateLimits.from.minute();
        }

        if (selectedDateFormatted === toDateFormatted) {
            endHour = dateLimits.to.hour();
            endMinute = dateLimits.to.minute();
        }

        if (selectedDate.isBefore(dateLimits.from, 'day') || selectedDate.isAfter(dateLimits.to, 'day')) {
            return options;
        }
    }

    for (let hour = 0; hour <= 23; hour++) {
        for (let minute = 0; minute < 60; minute += 15) {
            const isWithinStart = hour > startHour || (hour === startHour && minute >= startMinute);
            const isWithinEnd = hour < endHour || (hour === endHour && minute <= endMinute);

            if (isWithinStart && isWithinEnd) {
                const time = moment.tz(timeZone).hour(hour).minute(minute).second(0);
                const formattedTime = time.format('hh:mma');

                options.push({
                    id: formattedTime,
                    label: formattedTime
                });
            }
        }
    }

    return options;
};

export const getInitialDateWithValidTime = (
    options: Array<{ id: string; label: string }>,
    timeZone: string,
    dateLimits?: Period
): Moment => {
    const today = moment().tz(timeZone);

    if (options.length === 0) {
        return today;
    }

    const currentTime = today.format('hh:mma');

    const currentTimeOption = options.find(option => option.id === currentTime);

    if (currentTimeOption) {
        return today.clone().set({
            hour: moment.tz(currentTimeOption.id, 'hh:mma', timeZone).hour(),
            minute: moment.tz(currentTimeOption.id, 'hh:mma', timeZone).minute()
        });
    }

    const currentTimeMoment = moment.tz(currentTime, 'hh:mma', timeZone);
    let closestOption = options[0];
    let smallestDiff = Infinity;

    for (const option of options) {
        const optionTime = moment.tz(option.id, 'hh:mma', timeZone);
        const diff = Math.abs(optionTime.diff(currentTimeMoment, 'minutes'));

        if (diff < smallestDiff) {
            smallestDiff = diff;
            closestOption = option;
        }
    }

    if (dateLimits?.from && dateLimits?.to) {
        if (today.isBefore(dateLimits.from, 'day') || today.isAfter(dateLimits.to, 'day')) {
            const startDate = dateLimits.from.clone();
            return startDate.set({
                hour: moment.tz(options[0].id, 'hh:mma', timeZone).hour(),
                minute: moment.tz(options[0].id, 'hh:mma', timeZone).minute()
            });
        }
    }

    return today.clone().set({
        hour: moment.tz(closestOption.id, 'hh:mma', timeZone).hour(),
        minute: moment.tz(closestOption.id, 'hh:mma', timeZone).minute()
    });
};

interface FormattedDate {
    date: string;
    time: string;
}

export const formatPromoCodeDate = (momentDate: Moment): FormattedDate => {
    return {
        date: momentDate.format('MMM DD, YYYY'),
        time: momentDate.format('h:mm A')
    };
};
export const formatPromoCodeName = (promoCode: Promocode) => {
    const valueText =
        promoCode.discountType === 'fixed'
            ? `$${Number(promoCode.discountAmount).toFixed(2)} off`
            : `${Number(promoCode.discountAmount).toFixed(2)}% off`;

    return {
        name: promoCode.name,
        value: valueText
    };
};

/**
 * Converts a Promocode to SavePromocode format by removing read-only fields
 * and making id optional for API operations
 */
export const convertPromocodeToSavePromocode = (promocode: Promocode): SavePromocode => {
    return {
        id: promocode.id,
        name: promocode.name,
        discountType: promocode.discountType,
        discountAmount: promocode.discountAmount,
        startAt: promocode.startAt,
        endAt: promocode.endAt,
        maxCustomerRedeems: promocode.maxCustomerRedeems,
        maxTotalRedeems: promocode.maxTotalRedeems,
        active: promocode.active,
        public: promocode.public,
        olb: promocode.olb
    };
};

/**
 * Converts a Promocode to SavePromocode format with active field set to true
 */
export const convertPromocodeToActiveSavePromocode = (promocode: Promocode): SavePromocode => {
    return {
        ...convertPromocodeToSavePromocode(promocode),
        active: true
    };
};

/**
 * Converts a Promocode to SavePromocode format with active field set to false
 */
export const convertPromocodeToInactiveSavePromocode = (promocode: Promocode): SavePromocode => {
    return {
        ...convertPromocodeToSavePromocode(promocode),
        active: false
    };
};
